import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, ArrowLeft, Search, X, UserPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const itemTypes = ["Controller", "ECU/ECM", "Monitor Panel", "Controller Pump", "Modul", "Lain-lain"];

export default function IntakePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preselectId = params.get("customer");

  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);

  // Customer picker state
  const [pickedCustomer, setPickedCustomer] = useState(null); // full customer object
  const [pickedAddressId, setPickedAddressId] = useState("none");
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    item_type: "Controller",
    item_brand: "",
    complaint: "",
    estimated_cost: "",
    assigned_tech_id: "unassigned",
    notes: "",
  });

  useEffect(() => {
    api.get("/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (preselectId) {
      api.get(`/customers/${preselectId}`).then((r) => selectCustomer(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [preselectId]);

  useEffect(() => {
    if (pickedCustomer || custQuery.trim().length < 2) {
      setCustResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/customers", { params: { q: custQuery.trim() } });
        setCustResults(data.slice(0, 8));
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, pickedCustomer]);

  const selectCustomer = (c) => {
    setPickedCustomer(c);
    setCustQuery("");
    setCustResults([]);
    const def = (c.addresses || []).find((a) => a.is_default) || (c.addresses || [])[0];
    setPickedAddressId(def ? def.id : "none");
    setForm((f) => ({ ...f, customer_name: c.name, customer_phone: c.phone }));
  };

  const clearCustomer = () => {
    setPickedCustomer(null);
    setPickedAddressId("none");
    setForm((f) => ({ ...f, customer_name: "", customer_phone: "" }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.item_brand || !form.complaint) {
      toast.error("Lengkapi field wajib");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_cost: Number(form.estimated_cost || 0),
        assigned_tech_id: form.assigned_tech_id === "unassigned" ? null : form.assigned_tech_id,
        customer_id: pickedCustomer?.id || null,
        shipping_address_id:
          pickedCustomer && pickedAddressId !== "none" ? pickedAddressId : null,
      };
      const { data } = await api.post("/services", payload);
      toast.success(`Berhasil disimpan: ${data.ticket_no}`);
      nav(`/services/${data.id}`);
    } catch (err) {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="intake-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </Button>
      </div>

      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Tambah Barang Masuk
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Pilih customer yang sudah terdaftar atau isi data pelanggan baru.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6 space-y-5"
      >
        {/* Customer Picker */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-slate-900">Data Pelanggan</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => nav("/customers")}
              className="gap-1 text-xs h-7"
              data-testid="go-manage-customers"
            >
              <UserPlus className="w-3.5 h-3.5" /> Kelola Customer
            </Button>
          </div>

          {!pickedCustomer ? (
            <div className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="intake-customer-search"
                  placeholder="Cari customer terdaftar (nama / no HP)..."
                  value={custQuery}
                  onChange={(e) => setCustQuery(e.target.value)}
                  className="pl-9"
                />
                {custResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {custResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectCustomer(c)}
                        data-testid={`cust-result-${c.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900 text-sm">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.phone} · {c.addresses?.length || 0} alamat</div>
                      </button>
                    ))}
                  </div>
                )}
                {searching && custQuery.length >= 2 && custResults.length === 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs text-slate-500">
                    Mencari...
                  </div>
                )}
              </div>
              <div className="text-xs text-slate-400 text-center">— atau isi data baru di bawah —</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name">Nama Pelanggan *</Label>
                  <Input
                    id="customer_name"
                    data-testid="intake-customer-name"
                    value={form.customer_name}
                    onChange={(e) => set("customer_name", e.target.value)}
                    placeholder="Budi Santoso"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">No. HP / WhatsApp *</Label>
                  <Input
                    id="customer_phone"
                    data-testid="intake-customer-phone"
                    value={form.customer_phone}
                    onChange={(e) => set("customer_phone", e.target.value)}
                    placeholder="0812xxxxxxxx"
                    required
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-4" data-testid="intake-picked-customer">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center">
                    <Check className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{pickedCustomer.name}</div>
                    <div className="text-xs text-slate-600">{pickedCustomer.phone}{pickedCustomer.email ? ` · ${pickedCustomer.email}` : ""}</div>
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={clearCustomer} data-testid="clear-customer" className="h-8 gap-1">
                  <X className="w-3.5 h-3.5" /> Ganti
                </Button>
              </div>

              {(pickedCustomer.addresses || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200/60">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Alamat Pengiriman</Label>
                  <Select value={pickedAddressId} onValueChange={setPickedAddressId}>
                    <SelectTrigger data-testid="intake-address-select" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tanpa alamat pengiriman</SelectItem>
                      {pickedCustomer.addresses.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.label} — {a.full_address.slice(0, 40)}{a.full_address.length > 40 ? "..." : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {pickedAddressId !== "none" && (
                    <div className="mt-2 text-xs text-slate-600 bg-white/60 rounded px-2 py-1.5 whitespace-pre-line">
                      {(pickedCustomer.addresses.find((a) => a.id === pickedAddressId) || {}).full_address}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-display font-semibold text-slate-900 mb-3">Detail Barang</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="item_type">Jenis Barang *</Label>
              <Select value={form.item_type} onValueChange={(v) => set("item_type", v)}>
                <SelectTrigger data-testid="intake-item-type" id="item_type">
                  <SelectValue placeholder="Pilih jenis" />
                </SelectTrigger>
                <SelectContent>
                  {itemTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item_brand">Merek / Model *</Label>
              <Input
                id="item_brand"
                data-testid="intake-item-brand"
                value={form.item_brand}
                onChange={(e) => set("item_brand", e.target.value)}
                placeholder="Bosch EDC17"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="complaint">Keluhan / Kerusakan *</Label>
              <Textarea
                id="complaint"
                data-testid="intake-complaint"
                value={form.complaint}
                onChange={(e) => set("complaint", e.target.value)}
                placeholder="Mati total, no start, dsb."
                rows={3}
                required
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-display font-semibold text-slate-900 mb-3">Estimasi & Penanganan</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="estimated_cost">Estimasi Biaya (Rp)</Label>
              <Input
                id="estimated_cost"
                type="number"
                min="0"
                data-testid="intake-estimated-cost"
                value={form.estimated_cost}
                onChange={(e) => set("estimated_cost", e.target.value)}
                placeholder="150000"
              />
            </div>
            <div>
              <Label htmlFor="tech">Teknisi</Label>
              <Select value={form.assigned_tech_id} onValueChange={(v) => set("assigned_tech_id", v)}>
                <SelectTrigger data-testid="intake-technician" id="tech">
                  <SelectValue placeholder="Pilih teknisi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Belum ditugaskan</SelectItem>
                  {techs.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes">Catatan (opsional)</Label>
              <Textarea
                id="notes"
                data-testid="intake-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                placeholder="Kelengkapan aksesoris, kondisi awal, dll."
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button type="submit" disabled={saving} data-testid="intake-submit-button" className="bg-slate-900 hover:bg-slate-800 gap-2">
            <PlusCircle className="w-4 h-4" />
            {saving ? "Menyimpan..." : "Simpan Barang Masuk"}
          </Button>
          <Button type="button" variant="outline" onClick={() => nav("/services")}>Batal</Button>
        </div>
      </form>
    </div>
  );
}
